<script setup lang="ts">
import { useI18n } from '@/i18n';
import { SSO_LOAD_ERROR_CODE } from '@/lib/auth-api-error-codes';
import { isEmbeddedAuthContext } from '@/lib/authEmbedded';
import { GOOGLE_CLIENT_ID } from '@/lib/env';
import { loadGoogleIdentity, type GoogleCredentialResponse } from '@/lib/googleIdentity';
import { useAuthStore, type SignUpOptions } from '@/stores/auth';
import type { SsoSuccessPayload } from '@/use/authFlow';
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue';

/**
 * SSO sign-in buttons for the auth views (login + signup).
 *
 * Google renders its OFFICIAL button via `google.accounts.id.renderButton` —
 * required by Google's branding rules for the ID-token flow — which
 * self-localizes from the `locale` we pass. Structured internally as a provider
 * list so adding Apple later is additive: new entry in `providers`, new mount
 * function.
 *
 * Hidden entirely in embedded contexts (`isEmbeddedAuthContext`): Google blocks
 * OAuth inside embedded webviews/frames, so showing the button there would only
 * dead-end.
 */

type ProviderId = 'google';

const props = defineProps<{
  /** Picks the official button's label: `signin_with` vs `signup_with`. */
  mode: 'signin' | 'signup';
  /** Signup intent (trial / role) passed through to `ssoLogin` for new users. */
  signUpOptions?: SignUpOptions;
}>();

const emit = defineEmits<{
  success: [payload: SsoSuccessPayload];
  /**
   * API error code (`AUTH_FAILED`, `ACCOUNT_EXISTS`, …) or `SSO_FAILED` when
   * GIS breaks at credential time. Only emitted for failures of an attempted
   * sign-in — a mount-time load failure (ad blocker, offline) hides the
   * buttons silently instead of alarming users who never touched them.
   */
  error: [code: string];
}>();

const { t, locale } = useI18n();
const authStore = useAuthStore();

/** Computed once — the embedding cannot change without a full page load. */
const embedded = isEmbeddedAuthContext();

const providerEls = reactive<Partial<Record<ProviderId, HTMLElement | null>>>({});
const failed = ref<Partial<Record<ProviderId, boolean>>>({});

// Live button observers, disconnected on unmount. `disposed` guards the async
// provider mounts against an unmount that lands mid-load (see mountGoogleButton).
const resizeObservers: ResizeObserver[] = [];
let disposed = false;

const providers: { id: ProviderId; mount: (el: HTMLElement) => Promise<void> }[] = [
  { id: 'google', mount: mountGoogleButton },
];

/** Every provider failed to set up (e.g. the GIS script is blocked) — nothing to show. */
const allFailed = computed(() => providers.every((provider) => failed.value[provider.id]));

// The official Google button only accepts a fixed pixel width and does not
// reflow on its own, so we re-render it whenever the auth card resizes to keep
// it flush with the email/password fields. Google clamps the value to 200–400px;
// the card caps well under 400px today, but the documented ceiling stays explicit.
const GSI_MIN_WIDTH = 200;
const GSI_MAX_WIDTH = 400;

async function mountGoogleButton(el: HTMLElement) {
  const accountsId = await loadGoogleIdentity();
  // The component unmounted while the GIS script was still loading (login ↔
  // signup navigation): skip initializing and attaching a persistent observer to
  // a detached element that `onUnmounted` has already stopped tracking.
  if (disposed) return;

  accountsId.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: onGoogleCredential,
    context: props.mode === 'signup' ? 'signup' : 'signin',
  });

  // Re-render only when the clamped width actually changes — the button always
  // spans the card, and this guards against redundant renders (and any
  // ResizeObserver feedback loop).
  let lastWidth = 0;
  const render = () => {
    const width = Math.min(GSI_MAX_WIDTH, Math.max(GSI_MIN_WIDTH, Math.round(el.offsetWidth)));
    if (width === lastWidth) return;
    lastWidth = width;
    accountsId.renderButton(el, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: props.mode === 'signup' ? 'signup_with' : 'signin_with',
      logo_alignment: 'center',
      locale: locale.value,
      width,
    });
  };

  // Render once now, then track the card width. A one-shot measurement at mount
  // catches the layout before it settles (the card fades in) and locks the
  // button to Google's 200px minimum; the observer re-renders it at the real
  // width once laid out, and on every subsequent resize.
  render();
  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(render);
    // Observe the (unmutated) container, not `el` — `render()` replaces `el`'s
    // children, and observing the element you mutate is what surfaces the
    // "ResizeObserver loop completed with undelivered notifications" warning.
    observer.observe(el.parentElement ?? el);
    resizeObservers.push(observer);
  }
}

async function onGoogleCredential(response: GoogleCredentialResponse) {
  // `credential` is typed required, but GIS is an external script — stay defensive.
  const credential = response?.credential;
  if (!credential) {
    emit('error', SSO_LOAD_ERROR_CODE);
    return;
  }
  try {
    const result = await authStore.ssoLogin('google', credential, props.signUpOptions, props.mode);
    // The store's reentrancy guard collapsed the call (a sign-in is already
    // in flight) — drop this credential silently rather than double-handling.
    if (!result) return;
    emit('success', { user: result.user, isNewUser: result.isNewUser });
  } catch (e: unknown) {
    emit('error', e instanceof Error ? e.message : String(e));
  }
}

onMounted(async () => {
  if (embedded) return;
  await Promise.all(
    providers.map(async (provider) => {
      try {
        const el = providerEls[provider.id];
        if (!el) throw new Error(SSO_LOAD_ERROR_CODE);
        await provider.mount(el);
      } catch {
        // Silent: a provider that can't set up (GIS blocked by an ad blocker,
        // offline…) just hides itself — the password form is unaffected, so
        // raising the views' error Alert on page load would be a false alarm
        // for every blocked visitor. `error` stays reserved for failures of
        // an attempted sign-in (see `onGoogleCredential`).
        failed.value = { ...failed.value, [provider.id]: true };
      }
    }),
  );
});

onUnmounted(() => {
  disposed = true;
  resizeObservers.forEach((observer) => observer.disconnect());
});
</script>

<template>
  <div v-if="!embedded && !allFailed" class="auth-sso-buttons">
    <div class="auth-sso-buttons__providers">
      <div
        v-for="provider in providers"
        v-show="!failed[provider.id]"
        :key="provider.id"
        :ref="(el) => (providerEls[provider.id] = el as HTMLElement | null)"
        class="auth-sso-buttons__provider"
        :data-provider="provider.id"
      ></div>
    </div>
    <div class="auth-sso-buttons__divider" role="separator" aria-orientation="horizontal">
      {{ t('auth_or_divider') }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Sits at the top of the auth forms: providers first, then the "or" divider
// leading into the manual email/password fields below. Vertical spacing comes
// from the parent LayoutGap.
.auth-sso-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: space('small');
  width: 100%;

  &__divider {
    display: flex;
    align-items: center;
    gap: space('xsmall');
    width: 100%;
    color: theme('color-text-secondary');
    font-size: fontSize('small');

    &::before,
    &::after {
      content: '';
      flex: 1;
      border-top: 1px solid theme('color-border-primary');
    }
  }

  &__providers {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: space('small');
    width: 100%;
  }

  &__provider {
    display: flex;
    justify-content: center;
    width: 100%;
    // Reserve the official button's large-size height so the card doesn't
    // jump when GIS finishes rendering.
    min-height: 40px;
  }
}
</style>
